import {
    Button,
    Card,
    CardBody,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    HStack,
    Input,
    Select,
    Text,
    VStack,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import {
    DatePicker,
    MaskedPhoneInputControlled,
    MaskedSsnInputControlled,
    MaskedZipCodeInputControlled,
    RepeatShimmer,
    Shimmer,
    SingleSelectControlled,
    useToast,
} from "@am-tax/fe-core";
import { useCandidate } from "../../api/hooks/useCandidate";
import {
    CandidateQualification,
    CandidateStatus,
    displayEmploymentStatus,
    displayQualification,
    EmploymentStatus,
    EntityCandidateArgs,
    EntityCandidateForUpdate,
} from "../../api/models/EntityCandidate";
import { useCandidateSave } from "../../api/hooks/useCandidateSave";
import { getCandidateStatusTitle } from "../../components/CandidateStatus";
import { StateResponse, useStates } from "../../api";
import { useTenant } from "../../api/api";
import { FunctionComponent } from "react";
import { QualificationCheckboxes } from "./QualificationCheckboxes";
import globalEvents from "../../utils/globalEvents";
import { EmailInputControlled } from "@am-tax/wotc-common";

type FormValues = Partial<EntityCandidateForUpdate>;
type CandidateDetailsData = EntityCandidateForUpdate & {
    employmentEmployeeId?: string;
    employmentEIN?: string;
};

interface CandidateDetailsFormProps {
    returnTo: string;
}

export const CandidateDetailsForm: FunctionComponent<CandidateDetailsFormProps> = ({ returnTo }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const { cId, eId, candidateId } = useParams();
    const candidateQuery = useCandidate(cId, eId, candidateId);
    const candidate = (candidateQuery.data || {}) as CandidateDetailsData;
    const tenantQuery = useTenant(cId);
    const candidateFieldsConfiguration = tenantQuery.data?.candidateFieldsConfiguration;
    const refetchCandidateQuery = candidateQuery.refetch;
    const statesQuery = useStates();

    // ---- Code to Load the Form ----
    const formValues: FormValues = {
        ...candidate,
    };
    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
    } = useForm<FormValues>({
        values: formValues,
    });
    // ---- Setup Submit Handlers for saving the Form ----
    const saveCandidate = useCandidateSave();
    const onSubmit: SubmitHandler<FormValues> = async data => {
        const args: EntityCandidateArgs = {
            clientId: cId,
            entityId: eId,
            // @ts-ignore
            candidate: {
                ...candidate,
                ...data,
            },
        };
        saveCandidate.mutate(args, {
            onSuccess: async (data: any) => {
                await globalEvents.callAsyncEvent("updateCandidateQualifications");
                toast({
                    title: "Saved",
                    description: "Request Saved.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                await globalEvents.callAsyncEvent("refetchCandidateDetailsModalQuery");
                await globalEvents.callAsyncEvent("refetchCandidatePageQuery");
                await refetchCandidateQuery();
                goBackToCandidate();
            },
        });
    };
    const formSubmit = handleSubmit(onSubmit);

    // ---- Navigation Helper ----
    const goBackToCandidate = () => {
        navigate(`/clients/${cId}/candidates/${eId}/${candidateId}/${returnTo}`);
    };

    return (
        <>
            <VStack hidden={!candidateQuery.isLoading} spacing="1px" alignItems="stretch">
                <RepeatShimmer times={5} height="40px" />
            </VStack>

            {!candidateQuery.isLoading && (
                <form onSubmit={formSubmit}>
                    <fieldset disabled={saveCandidate.isLoading}>
                        <VStack gap={"1rem"} alignItems={"start"}>
                            <Flex gap={"1rem"} width={"100%"} alignItems={"end"}>
                                <FormControl isInvalid={!!errors?.firstName}>
                                    <FormLabel>Name:</FormLabel>
                                    <Input placeholder="First" {...register("firstName", { required: "First Name is required" })} />
                                    <FormErrorMessage>{errors?.firstName?.message}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!errors?.middleName}>
                                    <Input placeholder="Middle" {...register("middleName")} />
                                    <FormErrorMessage>{errors?.middleName?.message}</FormErrorMessage>
                                </FormControl>
                            </Flex>
                            <FormControl isInvalid={!!errors?.lastName}>
                                <Input placeholder={"Last"} {...register("lastName", { required: "Last Name is required" })} />
                                <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!errors?.status}>
                                <FormLabel>Status:</FormLabel>
                                <Select placeholder={"Select a Status"} {...register("status", { required: "Status is required." })}>
                                    {Object.values(CandidateStatus).map(status => (
                                        <option key={status} value={status}>
                                            {getCandidateStatusTitle(status)}
                                        </option>
                                    ))}
                                </Select>
                                <FormErrorMessage>{errors?.status?.message}</FormErrorMessage>
                            </FormControl>
                            <Divider mt={"1rem"} />
                            <HStack gap="2rem" width={"100%"} alignItems={"stretch"}>
                                <VStack gap="1rem" width={"100%"} alignItems={"stretch"}>
                                    <Heading variant={"info"}>Address</Heading>
                                    <FormControl isInvalid={!!errors?.street1}>
                                        <FormLabel>Street 1:</FormLabel>
                                        <Input placeholder={"1 Main Street"} {...register("street1")} />
                                        <FormErrorMessage>{errors?.street1?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={!!errors?.street2}>
                                        <FormLabel>Street 2:</FormLabel>
                                        <Input placeholder={"Street 2"} {...register("street2")} />
                                        <FormErrorMessage>{errors?.street2?.message}</FormErrorMessage>
                                    </FormControl>
                                    <HStack>
                                        <FormControl isInvalid={!!errors?.city}>
                                            <FormLabel>City:</FormLabel>
                                            <Input placeholder="City" {...register("city")} />
                                            <FormErrorMessage>{errors?.city?.message}</FormErrorMessage>
                                        </FormControl>
                                        <FormControl isInvalid={!!errors?.county}>
                                            <FormLabel>County:</FormLabel>
                                            <Input placeholder="County" {...register("county")} />
                                            <FormErrorMessage>{errors?.county?.message}</FormErrorMessage>
                                        </FormControl>
                                    </HStack>
                                    <HStack>
                                        <FormControl isInvalid={!!errors?.zipCode}>
                                            <FormLabel>Zip Code:</FormLabel>
                                            <MaskedZipCodeInputControlled control={control} name="zipCode" invalidMessage={"Zip code is invalid"} />
                                            <FormErrorMessage>{errors?.zipCode?.message}</FormErrorMessage>
                                        </FormControl>
                                        <FormControl isInvalid={!!errors?.state}>
                                            <FormLabel>State:</FormLabel>
                                            <Shimmer isLoading={statesQuery.isLoading}>
                                                <SingleSelectControlled
                                                    control={control}
                                                    name="state"
                                                    placeholder={"State"}
                                                    options={statesQuery.data?.map((state: StateResponse) => ({
                                                        label: state.name,
                                                        value: state.code,
                                                    }))}
                                                />
                                            </Shimmer>
                                            <FormErrorMessage>{errors?.state?.message}</FormErrorMessage>
                                        </FormControl>
                                    </HStack>
                                </VStack>
                                <VStack gap="1rem" width={"100%"} alignItems={"stretch"}>
                                    <Heading variant={"info"}>Personal Info</Heading>
                                    <FormControl isInvalid={!!errors?.phone}>
                                        <FormLabel>Phone:</FormLabel>
                                        <MaskedPhoneInputControlled control={control} name="phone" invalidMessage={"Phone Number is invalid"} />
                                        <FormErrorMessage>{errors?.phone?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={!!errors?.email}>
                                        <FormLabel>Email:</FormLabel>
                                        <EmailInputControlled
                                            control={control}
                                            name="email"
                                            // NOTE: TASK 34163: email is not required anymore: rules={{ required: "Email is required" }}
                                            invalidMessage={"Email is invalid"}
                                            placeholder={"me@email.com"}
                                        />
                                        <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={!!errors?.socialSecurityNumber}>
                                        <FormLabel>Social Security Number:</FormLabel>
                                        <MaskedSsnInputControlled
                                            control={control}
                                            name="socialSecurityNumber"
                                            rules={{ required: "Social Security Number is required" }}
                                            invalidMessage={"Social Security Number is invalid"}
                                        />
                                        <FormErrorMessage>{errors?.socialSecurityNumber?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={!!errors?.dateOfBirth}>
                                        <FormLabel>Date of Birth:</FormLabel>
                                        <DatePicker {...register("dateOfBirth")} />
                                        <FormErrorMessage>{errors?.dateOfBirth?.message}</FormErrorMessage>
                                    </FormControl>
                                </VStack>
                            </HStack>
                            <Divider mt={"1rem"} />
                            <Heading variant={"info"}>Eligibility</Heading>
                            <FormControl isInvalid={!!errors?.questionnaireCompletedOn}>
                                <FormLabel>Questionnaire Completion Date:</FormLabel>
                                <DatePicker {...register("questionnaireCompletedOn")} />
                                <FormErrorMessage>{errors?.questionnaireCompletedOn?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!errors?.qualifications}>
                                <FormLabel>Target Qualification:</FormLabel>
                                <Select placeholder={"Select a Qualification"} {...register("selectedQualification")}>
                                    {(candidate.qualifications ?? []).length > 0 && (
                                        <optgroup label="Qualified">
                                            {(candidate.qualifications ?? []).map(q => (
                                                <option key={q} value={q}>
                                                    {displayQualification(q as CandidateQualification)}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    <optgroup label="All Other Qualifications">
                                        {Object.values(CandidateQualification)
                                            .filter(q => !(candidate.qualifications ?? []).includes(q))
                                            .map((qualification: CandidateQualification) => (
                                                <option key={qualification} value={qualification}>
                                                    {displayQualification(qualification)}
                                                </option>
                                            ))}
                                    </optgroup>
                                </Select>
                                <FormErrorMessage>{errors?.qualifications?.message}</FormErrorMessage>
                            </FormControl>
                            <Card width={"100%"}>
                                <CardBody>
                                    <VStack spacing=".5em" justifyContent="left" alignItems={"stretch"}>
                                        <QualificationCheckboxes clientId={cId} candidateId={candidateId} entityId={eId} tenantId={cId} />
                                    </VStack>
                                </CardBody>
                            </Card>
                            <Divider mt={"1rem"} />
                            <Heading variant={"info"}>Employment</Heading>
                            <FormControl isInvalid={!!errors?.employmentStatus}>
                                <FormLabel>Employment Status:</FormLabel>
                                <SingleSelectControlled
                                    control={control}
                                    name="employmentStatus"
                                    placeholder={"Choose Employment Status"}
                                    options={Object.values(EmploymentStatus).map((status: EmploymentStatus) => ({
                                        label: displayEmploymentStatus(status),
                                        value: status,
                                    }))}
                                />
                                <FormErrorMessage>{errors?.employmentStatus?.message}</FormErrorMessage>
                            </FormControl>
                            <HStack gap="2rem" width={"100%"}>
                                <FormControl>
                                    <FormLabel variant={"label"}>Employee Number:</FormLabel>
                                    <Text>{candidate?.employmentEmployeeId}</Text>
                                </FormControl>
                                <FormControl>
                                    <FormLabel variant={"label"}>Employer Identification Number:</FormLabel>
                                    <Text>{candidate?.employmentEIN}</Text>
                                </FormControl>
                            </HStack>
                            <FormControl>
                                <FormLabel variant={"label"}>Job Title:</FormLabel>
                                <Input type={"text"} {...register("employmentJobTitle")} />
                                <FormErrorMessage>{errors?.employmentJobTitle?.message}</FormErrorMessage>
                            </FormControl>
                            <HStack gap="2rem" width={"100%"} alignItems={"stretch"}>
                                <FormControl isInvalid={!!errors?.employmentHireDate}>
                                    <FormLabel>Hire Date:</FormLabel>
                                    <DatePicker {...register("employmentHireDate")} />
                                    <FormErrorMessage>{errors?.employmentHireDate?.message}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!errors?.employmentStartDate}>
                                    <FormLabel>Start Date:</FormLabel>
                                    <DatePicker {...register("employmentStartDate")} />
                                    <FormErrorMessage>{errors?.employmentStartDate?.message}</FormErrorMessage>
                                </FormControl>
                            </HStack>
                            <HStack gap="2rem" width={"100%"} alignItems={"stretch"}>
                                <FormControl isInvalid={!!errors?.employmentOfferDate}>
                                    <FormLabel>Offer Date:</FormLabel>
                                    <DatePicker {...register("employmentOfferDate")} />
                                    <FormErrorMessage>{errors?.employmentOfferDate?.message}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!errors?.employmentTerminationDate}>
                                    <FormLabel>Termination Date:</FormLabel>
                                    <DatePicker {...register("employmentTerminationDate")} />
                                    <FormErrorMessage>{errors?.employmentTerminationDate?.message}</FormErrorMessage>
                                </FormControl>
                            </HStack>
                            {[1, 2, 3, 4, 5].some(n => {
                                const key = `customField0${n}DisplayName` as keyof typeof candidateFieldsConfiguration;
                                return !!candidateFieldsConfiguration?.[key];
                            }) && (
                                <>
                                    <Divider mt={"1rem"} />
                                    <Heading variant={"info"}>Additional</Heading>
                                    {[1, 2, 3, 4, 5].map(n => {
                                        const nameKey = `customField0${n}DisplayName` as keyof typeof candidateFieldsConfiguration;
                                        const displayName = candidateFieldsConfiguration?.[nameKey] as string | undefined;
                                        if (!displayName) return null;
                                        return (
                                            <FormControl key={n}>
                                                <FormLabel variant={"label"}>{displayName}</FormLabel>
                                                <Input type={"text"} {...register(`candidateCustomFields.customField0${n}` as any)} />
                                            </FormControl>
                                        );
                                    })}
                                </>
                            )}
                            <Flex justifyContent="flex-end" mt={"2rem"} width={"full"}>
                                <Button
                                    type="submit"
                                    variant={"primary"}
                                    mr={3}
                                    isLoading={saveCandidate.isLoading}
                                    isDisabled={saveCandidate.isLoading || Object.keys(errors).length > 0}
                                    loadingText={"Saving"}
                                >
                                    Save
                                </Button>
                                <Button variant="ghost" onClick={goBackToCandidate}>
                                    Cancel
                                </Button>
                            </Flex>
                        </VStack>
                    </fieldset>
                </form>
            )}
        </>
    );
};
